import React from 'react';
import {View, Text, TouchableOpacity, ScrollView} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {styles} from '../../styles';
import {SHORTCUT_GROUPS, INFO_GROUPS} from '../../constants/shortcutsHelp';

export type HelpModalTab = 'shortcuts' | 'info';

type HelpModalProps = {
  visible: boolean;
  onClose: () => void;
  activeTab: HelpModalTab;
  onTabChange: (tab: HelpModalTab) => void;
};

// The "?" help modal: general usage guide + keyboard shortcuts reference.
function HelpModal({visible, onClose, activeTab, onTabChange}: HelpModalProps) {
  return (
    <SlideModal
      visible={visible}
      onClose={onClose}
      type="center"
      showHeader={false}
      contentPadding={0}
      customStyle={{
        borderRadius: 20,
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        width: 820,
        maxWidth: '92%',
        alignSelf: 'center',
        borderWidth: 0,
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 10},
        shadowOpacity: 0.08,
        shadowRadius: 20,
        elevation: 5,
      }}>
      {/* Header Block */}
      <View
        style={{
          backgroundColor: '#2C1A11',
          paddingHorizontal: 20,
          paddingVertical: 14,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            flex: 1,
            marginRight: 24,
          }}>
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              backgroundColor: '#402C1F',
              borderWidth: 1,
              borderColor: '#B58E58',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <MaterialCommunityIcons
              name="chess-knight"
              size={16}
              color="#FFFFFF"
            />
          </View>
          <View style={{flex: 1}}>
            <Text
              style={{
                fontSize: 14,
                fontWeight: 'bold',
                color: '#FFFFFF',
                letterSpacing: -0.2,
              }}>
              Help & Shortcuts
            </Text>
            <Text style={{fontSize: 10.5, color: '#A39081', marginTop: 1}}>
              Every control on this board, one square away.
            </Text>
          </View>
        </View>

        <TouchableOpacity
          onPress={onClose}
          activeOpacity={0.8}
          style={{
            width: 26,
            height: 26,
            borderRadius: 13,
            backgroundColor: 'rgba(255,255,255,0.08)',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Ionicons name="close-outline" size={15} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {/* Tabs Selector */}
      <View
        style={[
          styles.workspaceTabRow,
          {
            paddingHorizontal: 20,
            paddingTop: 14,
            borderBottomWidth: 1,
            borderBottomColor: '#F5F5F5',
          },
        ]}>
        <TouchableOpacity
          onPress={() => onTabChange('info')}
          style={[
            styles.workspaceTabBtn,
            activeTab === 'info' && styles.workspaceTabBtnActive,
          ]}>
          <Text
            style={[
              styles.workspaceTabLabel,
              activeTab === 'info' && styles.workspaceTabLabelActive,
              {fontSize: 12.5, fontWeight: 'bold'},
            ]}>
            General Info
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => onTabChange('shortcuts')}
          style={[
            styles.workspaceTabBtn,
            activeTab === 'shortcuts' && styles.workspaceTabBtnActive,
          ]}>
          <Text
            style={[
              styles.workspaceTabLabel,
              activeTab === 'shortcuts' && styles.workspaceTabLabelActive,
              {fontSize: 12.5, fontWeight: 'bold'},
            ]}>
            Shortcuts
          </Text>
        </TouchableOpacity>
      </View>

      {/* Body Container */}
      <View style={{padding: 20, height: 280}}>
        {activeTab === 'shortcuts' ? (
          <View style={{flexDirection: 'row', gap: 24, flex: 1}}>
            {SHORTCUT_GROUPS.map(group => (
              <View key={group.title} style={{flex: 1, gap: 8}}>
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: 'bold',
                    color: '#B58E58',
                    letterSpacing: 0.5,
                  }}>
                  {group.title.toUpperCase()}
                </Text>
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  style={{flex: 1}}>
                  <View style={{gap: 6}}>
                    {group.items.map(([key, label]) => (
                      <View
                        key={key}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingVertical: 1.5,
                        }}>
                        <Text
                          style={{
                            fontSize: 11,
                            color: '#6B584D',
                            flex: 1,
                            marginRight: 8,
                          }}>
                          {label}
                        </Text>
                        <View
                          style={{
                            backgroundColor: '#FFFFFF',
                            borderWidth: 1,
                            borderColor: 'rgba(44, 26, 17, 0.2)',
                            borderBottomWidth: 2.5,
                            borderRadius: 4,
                            paddingHorizontal: 6,
                            paddingVertical: 2,
                            minWidth: 24,
                            alignItems: 'center',
                          }}>
                          <Text
                            style={{
                              fontSize: 9,
                              fontWeight: 'bold',
                              color: '#2C1A11',
                            }}>
                            {key}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </View>
                </ScrollView>
              </View>
            ))}
          </View>
        ) : (
          <View style={{flexDirection: 'row', gap: 24, flex: 1}}>
            {/* Column 1: Board & Moves + Chapters */}
            <View style={{flex: 1, gap: 12}}>
              <ScrollView showsVerticalScrollIndicator={false}>
                <View style={{gap: 12}}>
                  {INFO_GROUPS.slice(0, 2).map(group => (
                    <View key={group.title} style={{gap: 6}}>
                      <Text
                        style={{
                          fontSize: 11,
                          fontWeight: 'bold',
                          color: '#B58E58',
                          letterSpacing: 0.5,
                        }}>
                        {group.title.toUpperCase()}
                      </Text>
                      <View style={{gap: 4}}>
                        {group.items.map(item => (
                          <View
                            key={item}
                            style={{
                              flexDirection: 'row',
                              gap: 6,
                              alignItems: 'flex-start',
                            }}>
                            <Text
                              style={{
                                fontSize: 11,
                                color: '#A5792F',
                                lineHeight: 14,
                              }}>
                              •
                            </Text>
                            <Text
                              style={{
                                fontSize: 11,
                                color: '#6B584D',
                                flex: 1,
                                lineHeight: 14,
                              }}>
                              {item}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Divider */}
            <View style={{width: 1, backgroundColor: '#F5F5F5'}} />

            {/* Column 2: Participants + Owner Tools */}
            <View style={{flex: 1, gap: 12}}>
              <ScrollView showsVerticalScrollIndicator={false}>
                <View style={{gap: 12}}>
                  {INFO_GROUPS.slice(2).map(group => (
                    <View key={group.title} style={{gap: 6}}>
                      <Text
                        style={{
                          fontSize: 11,
                          fontWeight: 'bold',
                          color: '#B58E58',
                          letterSpacing: 0.5,
                        }}>
                        {group.title.toUpperCase()}
                      </Text>
                      <View style={{gap: 4}}>
                        {group.items.map(item => (
                          <View
                            key={item}
                            style={{
                              flexDirection: 'row',
                              gap: 6,
                              alignItems: 'flex-start',
                            }}>
                            <Text
                              style={{
                                fontSize: 11,
                                color: '#A5792F',
                                lineHeight: 14,
                              }}>
                              •
                            </Text>
                            <Text
                              style={{
                                fontSize: 11,
                                color: '#6B584D',
                                flex: 1,
                                lineHeight: 14,
                              }}>
                              {item}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              </ScrollView>
            </View>
          </View>
        )}
      </View>

      {/* Footer */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-end',
          paddingHorizontal: 20,
          paddingBottom: 14,
          paddingTop: 10,
          borderTopWidth: 1,
          borderColor: '#F5F5F5',
        }}>
        <TouchableOpacity
          onPress={onClose}
          activeOpacity={0.8}
          style={{
            backgroundColor: '#FACF47',
            borderRadius: 100,
            paddingHorizontal: 20,
            paddingVertical: 6,
          }}>
          <Text style={{fontSize: 11.5, fontWeight: 'bold', color: '#1E110A'}}>
            Got it
          </Text>
        </TouchableOpacity>
      </View>
    </SlideModal>
  );
}

export default HelpModal;
